/**
 * Summary of the sidebar selections (status chip and filter groups), shown above the action buckets.
 * An applied chip that the draft still has keeps the accent style. A selection that is not yet
 * applied is grey. An applied filter cleared in the draft stays visible, lighter, and its X does
 * nothing until Apply. Clearing any other chip updates the draft only.
 */
import { observer } from "mobx-react";
import FontBody2 from "../../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../../../../../components/fonts/font-props";
import IconClose from "../../../../../../../components/icons/icon-close/icon-close";
import useDashboardTranslation from "../../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { useDateTimeFormattingParams } from "../../../../../../../hooks/use-datetime-formatting-params";
import RmaListFiltersStore, {
    SELECTED_FILTER_CHIP,
    SELECTED_FILTER_CHIP_STATE,
} from "../../../../../rma-list-page/stores/rma-list-filters-store";
import "./rma-list-selected-filters.scss";

const CHIP_STATE_CLASS: Record<SELECTED_FILTER_CHIP_STATE, string> = {
    [SELECTED_FILTER_CHIP_STATE.APPLIED]: "",
    [SELECTED_FILTER_CHIP_STATE.DRAFT]: " rma-list-selected-filters__chip--draft",
    [SELECTED_FILTER_CHIP_STATE.REMOVED]: " rma-list-selected-filters__chip--removed",
};

const CHIP_ICON_COLOR: Record<SELECTED_FILTER_CHIP_STATE, string> = {
    [SELECTED_FILTER_CHIP_STATE.APPLIED]: "var(--color-accent-1)",
    [SELECTED_FILTER_CHIP_STATE.DRAFT]: "var(--color-neutral-3)",
    [SELECTED_FILTER_CHIP_STATE.REMOVED]: "var(--color-neutral-7)",
};

interface IProps {
    rmaListFiltersStore: RmaListFiltersStore;
    disabled?: boolean;
}

const RmaListSelectedFilters: React.FC<IProps> = observer(({ rmaListFiltersStore, disabled }) => {
    const { getTranslation } = useDashboardTranslation();
    const { timeZone } = useDateTimeFormattingParams();
    const scope = "dashboard-returns-page";
    const translate = (key: string) => getTranslation(key, scope);
    const chips = rmaListFiltersStore.collectSelectedFilterChips(translate, timeZone);

    if (chips.length === 0) {
        return null;
    }

    const onRemove = (chipId: SELECTED_FILTER_CHIP) => {
        if (disabled) {
            return;
        }
        rmaListFiltersStore.clearSelectedFilterChip(chipId);
    };

    return (
        <div className={`rma-list-selected-filters${disabled ? " disabled" : ""}`}>
            <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                {`${getTranslation("SELECTED_FILTERS", scope)} (${chips.length})`}
            </FontBody2>
            <div className="rma-list-selected-filters__chips">
                {chips.map((chip) => {
                    const removed = chip.state === SELECTED_FILTER_CHIP_STATE.REMOVED;
                    const canRemove = !disabled && !removed;
                    return (
                        <div
                            className={`rma-list-selected-filters__chip${CHIP_STATE_CLASS[chip.state]}`}
                            key={`${chip.id}-${chip.state}`}
                        >
                            <span className="rma-list-selected-filters__chip-label">{chip.label}</span>
                            <span
                                role="button"
                                tabIndex={canRemove ? 0 : -1}
                                aria-disabled={!canRemove}
                                aria-label={`Clear ${chip.label}`}
                                className="rma-list-selected-filters__chip-remove"
                                onClick={canRemove ? () => onRemove(chip.id) : undefined}
                                onKeyDown={(e) => {
                                    if (!canRemove) {
                                        return;
                                    }
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onRemove(chip.id);
                                    }
                                }}
                            >
                                <IconClose color={CHIP_ICON_COLOR[chip.state]} />
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

export default RmaListSelectedFilters;
